import { useMemo } from "react";

import dayjs from "dayjs";

import { useTranslate } from "@similarweb/i18n-provider";
import { Banner, Month } from "@similarweb/icons";
import type { ActivationAlertsContractSocialAdAlert } from "@similarweb/pro-api-client";
import { shortMonthAndDay, fullMonthDayAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Card, Tooltip } from "@similarweb/ui-components";
import { Flex, Stack, TruncatedText } from "@similarweb/ui-components/foundations";

import { ImagePlaceholder } from "../../../common/image-fallback/ImagePlaceholder";
import { ImageWithFallback } from "../../../common/image-fallback/ImageWithFallback";
import { InfoItem } from "../../../common/InfoItem";
import { OpenInNewTab } from "../../../common/OpenInNewTab";
import { PreviewCard } from "../../../common/preview-card/PreviewCard";
import { FooterWrapper, ImageContainer } from "../../../common/styledComponents";

import { PlatformIconStack } from "./PlatformIconStack";

export const socialAdCardSize = {
    width: 360,
    height: 300,
};

type SocialAdCardProps = {
    ad: ActivationAlertsContractSocialAdAlert;
    onClick: () => void;
};

export const SocialAdCard = ({ ad, onClick }: SocialAdCardProps) => {
    const { translate } = useTranslate();
    const { fullDate, shortDate } = useMemo(() => {
        const date = dayjs(ad.firstSeen);

        return {
            fullDate: date.format(fullMonthDayAndYear),
            shortDate: date.format(shortMonthAndDay),
        };
    }, [ad.firstSeen]);

    return (
        <PreviewCard
            onClick={onClick}
            data-automation="social-ad-card"
            style={socialAdCardSize}
        >
            <Card.Header
                flexGrow={1}
                alignItems="flex-start"
            >
                <Stack
                    gap="12px"
                    paddingY="16px"
                    width="100%"
                >
                    {ad.title ? (
                        <TruncatedText
                            lineClamp={ad.body ? 1 : 2}
                            fontVariant="subtitle2"
                            color={colorsPalettes.carbon[500]}
                        >
                            {ad.title}
                        </TruncatedText>
                    ) : null}
                    {ad.body ? (
                        <TruncatedText
                            lineClamp={2}
                            fontVariant="body2"
                            color={colorsPalettes.carbon[400]}
                        >
                            {ad.body}
                        </TruncatedText>
                    ) : null}
                </Stack>
            </Card.Header>
            <ImageContainer paddingX="16px">
                <ImageWithFallback
                    src={ad.imageUrl}
                    alt={ad.title ?? "Social ad creative"}
                    fallback={<ImagePlaceholder />}
                />
            </ImageContainer>
            <FooterWrapper data-automation="ad-card-footer">
                <Flex gap="12px">
                    {ad.platforms?.length ? <PlatformIconStack platforms={ad.platforms} /> : null}
                    <InfoItem
                        icon={Banner}
                        title="Image"
                        tooltip={translate("activation.alerts.cards.social-post.creative-type", { type: "Image" })}
                    />
                    <InfoItem
                        icon={Month}
                        title={shortDate}
                        tooltip={translate("activation.alerts.cards.social-post.first-seen", {
                            date: fullDate,
                        })}
                    />
                </Flex>
                <Tooltip content={translate("activation.alerts.cards.ads.landing-page")}>
                    <OpenInNewTab
                        url={ad.landingPage}
                        data-automation="open-landing-page-button"
                    />
                </Tooltip>
            </FooterWrapper>
        </PreviewCard>
    );
};
